import { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';

import {
  ticketDocumentSet,
  ticketDocumentVersionsSet,
  ticketSummarySet,
} from '~/actions/ticket';
import { getTicketDocumentVersions } from '~/api/tickets';
import binocularsSrc from '~/assets/images/cta/binoculars.svg';
import flagSrc from '~/assets/images/cta/flag.svg';
import { getStageConfig } from '~/common/constants/ticket';
import { getNextStageButtonText } from '~/components/Workflow/TicketsViewPage/TicketDocumentActions/TicketDocumentActions';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { TicketPermissionType } from '~/enums/TicketPermissionType';
import { TicketStageType } from '~/enums/TicketStageType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { withUsers } from '~/hocs/withUsers';
import { FormattedMessage, useIntl } from 'react-intl';
import { getIsNextStageDisabled } from '~/reducers/ticket';
import { api } from '~/redux/api/createApi';
import { useRouting } from '~/routing/useRouting';
import CtaSection from '~/ui/components/CtaSection/CtaSection';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import WizardPageLayout from '~/ui/components/WizardPageLayout/WizardPageLayout';
import { getEnabledStages, getNextStageType } from '~/utils/ticket';

import SendTicketActionCta from './SendTicketActionCta';
import ShareAndDownloadCta from './ShareAndDownloadCta';

/* UX wants the title to take half of the space to the left of the Select Workflow header, which should be 325px. */
const TITLE_MAX_WIDTH = 325;

const stageToViewPermissionMap = {
  [TicketStageType.Edit]: TicketPermissionType.EditView,
  [TicketStageType.Review]: TicketPermissionType.ReviewView,
  [TicketStageType.Sign]: TicketPermissionType.SignView,
  [TicketStageType.Finalize]: TicketPermissionType.FinalizeView,
  [TicketStageType.Ticket]: TicketPermissionType.TicketView,
};

const WizardActionPage = ({
  ticketId,
  intakeForm,
  // connected
  ticket,
  users,
}) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const isEsignatureV2RevampEnabled = useFlag(FlagType.EsignatureV2Revamp);
  const intl = useIntl();
  const { navigate } = useRouting();

  const stageToStringMap = {
    [TicketStageType.Review]: intl.formatMessage({
      id: 'WizardActionPage.reviewStage',
      defaultMessage: 'Review',
      description: 'Review stage label',
    }),
    [TicketStageType.Sign]: intl.formatMessage({
      id: 'WizardActionPage.signaturesStage',
      defaultMessage: 'Signatures',
      description: 'Signatures stage label',
    }),
    [TicketStageType.Finalize]: intl.formatMessage({
      id: 'WizardActionPage.finalizationStage',
      defaultMessage: 'Finalization',
      description: 'Finalization stage label',
    }),
  };

  const { isCompleted, permissions, stage } = ticket;
  const shouldShowShare = permissions?.includes(TicketPermissionType.Share);
  const shouldShowDownload = permissions?.includes(
    TicketPermissionType.Download,
  );
  const shouldShowShareAndDownloadCta = shouldShowShare || shouldShowDownload;

  const stages = getEnabledStages(ticket, intl);
  const isNextStageDisabled = getIsNextStageDisabled(ticket, users);
  const nextStageText = getNextStageButtonText(
    stage,
    stages,
    isEsignatureV2RevampEnabled,
    intl,
  );
  const sendActionPermission =
    permissions?.includes(TicketPermissionType.SendForReview) ||
    permissions?.includes(TicketPermissionType.SendForSignatures) ||
    permissions?.includes(TicketPermissionType.SendForFinalization);
  const shouldShowSendTicketActionCta =
    nextStageText && !isNextStageDisabled && sendActionPermission;

  let requiredViewPermission = stageToViewPermissionMap[stage];
  if (stage === TicketStageType.Finalize && isCompleted) {
    requiredViewPermission = TicketPermissionType.CompletedView;
  }
  const shouldShowViewTicketCta = permissions?.includes(requiredViewPermission);

  const ctaCount =
    shouldShowSendTicketActionCta +
    shouldShowShareAndDownloadCta +
    shouldShowViewTicketCta;
  const hasOnlyOneCta = ctaCount === 1;
  const StageConfig = getStageConfig(intl);
  const stageString = StageConfig[stage]?.label;
  const nextStageType = getNextStageType(stage, stages);
  const nextStageString = stageToStringMap[nextStageType];
  const ticketStatus =
    stage === TicketStageType.Ticket
      ? intl.formatMessage({
          id: 'WizardActionPage.ticketCreated',
          defaultMessage: 'Your ticket has been created',
          description: 'Message when ticket is created',
        })
      : intl.formatMessage(
          {
            id: 'WizardActionPage.ticketCreatedInStage',
            defaultMessage:
              'Your ticket has been created in the {stageString} stage',
            description:
              'Message when ticket is created in a specific stage, Example: stageString = "Review"',
          },
          { stageString },
        );
  const ctaTitle = hasOnlyOneCta && ticketStatus;
  const pageDescription = ctaCount > 1 && (
    <>
      <p>{ticketStatus}</p>
      <p>
        <FormattedMessage
          id="WizardActionPage.howToProceed"
          defaultMessage="How would you like to proceed?"
          description="Question asking user how they want to proceed"
        />
      </p>
    </>
  );
  let viewTicketDescription = intl.formatMessage({
    id: 'WizardActionPage.viewTicketDescription',
    defaultMessage: 'View your ticket',
    description: 'Base description for view ticket action',
  });
  if (stage !== TicketStageType.Review && nextStageString) {
    viewTicketDescription += ` ${intl.formatMessage(
      {
        id: 'WizardActionPage.beforeSubmittingFor',
        defaultMessage: 'before submitting it for {nextStageString}.',
        description:
          'Additional text for view ticket description when there is a next stage',
      },
      { nextStageString },
    )}`;
  }

  const getDocumentVersions = async (documentId) => {
    const upToDateVersions = await getTicketDocumentVersions(documentId);
    dispatch(ticketDocumentVersionsSet(upToDateVersions));
  };

  // loading ticket information

  const {
    data: ticketDocumentData = {},
    isSuccess: isTicketDocumentSuccess,
    fulfilledTimeStamp: ticketDocumentFulfilledTimeStamp,
  } = api.endpoints.getTicketDocument.useQuery({
    ticketId,
  });

  const {
    data: ticketSummaryData = {},
    isSuccess: isTicketSummarySuccess,
    fulfilledTimeStamp: ticketSummaryFulfilledTimeStamp,
  } = api.endpoints.getTicketSummary.useQuery({
    ticketId,
  });

  const handleTicketError = () => {
    toast({
      message: intl.formatMessage({
        id: 'WizardActionPage.ticketLoadError',
        defaultMessage: 'There has been an error when loading this ticket.',
        description: 'Error message when ticket fails to load',
      }),
      type: 'error',
    });
  };

  // onSuccess for the ticketDocument fetch
  useEffect(() => {
    if (isTicketDocumentSuccess) {
      try {
        dispatch(ticketDocumentSet(ticketDocumentData));
        getDocumentVersions(ticketDocumentData.id);
      } catch {
        handleTicketError();
      }
    }
  }, [ticketDocumentFulfilledTimeStamp]);

  // onSuccess for the ticketSummary fetch
  useEffect(() => {
    if (isTicketSummarySuccess) {
      try {
        dispatch(ticketSummarySet(ticketSummaryData));
      } catch {
        handleTicketError();
      }
    }
  }, [ticketSummaryFulfilledTimeStamp]);

  const handeViewTicket = () => {
    navigate(`/workflow/tickets/${ticketId}`);
  };

  const handleClose = () => {
    navigate('/workflow/tickets');
  };

  if (ctaCount === 0) {
    return (
      <WizardPageLayout
        currentStep={intl.formatMessage({
          id: 'WizardActionPage.createTicketStep',
          defaultMessage: 'Create Ticket',
          description: 'Create ticket step label',
        })}
        steps={[
          intl.formatMessage({
            id: 'WizardActionPage.selectWorkflowStep',
            defaultMessage: 'Select Workflow',
            description: 'Select workflow step label',
          }),
          intl.formatMessage({
            id: 'WizardActionPage.createTicketStep',
            defaultMessage: 'Create Ticket',
            description: 'Create ticket step label',
          }),
        ]}
        title={{ text: intakeForm.name, maxWidth: TITLE_MAX_WIDTH }}
        onClose={handleClose}
      >
        <CtaSection
          actionButton={{
            text: intl.formatMessage({
              id: 'WizardActionPage.returnToTickets',
              defaultMessage: 'Return to Tickets',
              description: 'Button text to return to tickets list',
            }),
            onClick: handleClose,
          }}
          imageSrc={flagSrc}
          title={intl.formatMessage(
            {
              id: 'WizardActionPage.ticketCreatedAndSaved',
              defaultMessage:
                'Your ticket has been created and will be saved in the {stageString} stage.',
              description:
                'Message when ticket is created and saved in a stage Example: StageString = "Review"',
            },
            { stageString },
          )}
        />
      </WizardPageLayout>
    );
  }

  return (
    <WizardPageLayout
      currentStep={intl.formatMessage({
        id: 'WizardActionPage.takeActionStep',
        defaultMessage: 'Take Action',
        description: 'Take action step label',
      })}
      steps={[
        intl.formatMessage({
          id: 'WizardActionPage.selectWorkflowStep',
          defaultMessage: 'Select Workflow',
          description: 'Select workflow step label',
        }),
        intl.formatMessage({
          id: 'WizardActionPage.createTicketStep',
          defaultMessage: 'Create Ticket',
          description: 'Create ticket step label',
        }),
        intl.formatMessage({
          id: 'WizardActionPage.takeActionStep',
          defaultMessage: 'Take Action',
          description: 'Take action step label',
        }),
      ]}
      title={{ text: intakeForm.name, maxWidth: TITLE_MAX_WIDTH }}
      onClose={handleClose}
      description={pageDescription}
    >
      <FlexLayout spacing={3} justifyContent="center">
        {shouldShowSendTicketActionCta && (
          <SendTicketActionCta
            ticketId={ticketId}
            nextStageString={nextStageString}
            title={ctaTitle}
          />
        )}
        {shouldShowShareAndDownloadCta && (
          <ShareAndDownloadCta
            ticketId={ticketId}
            shouldShowShare={shouldShowShare}
            shouldShowDownload={shouldShowDownload}
            stage={stage}
            nextStageString={nextStageString}
            title={ctaTitle}
          />
        )}
        {shouldShowViewTicketCta && (
          <CtaSection
            actionButton={{
              text: intl.formatMessage({
                id: 'WizardActionPage.viewButton',
                defaultMessage: 'View',
                description: 'Button text to view ticket',
              }),
              onClick: handeViewTicket,
            }}
            title={ctaTitle}
            description={viewTicketDescription}
            imageSrc={binocularsSrc}
          />
        )}
      </FlexLayout>
    </WizardPageLayout>
  );
};

const mapStateToProps = ({ ticket }) => ({
  ticket,
});

export default withUsers(connect(mapStateToProps)(WizardActionPage));
